import { storageService } from "@/services/storage/service";
import type { LookPresetMetadata } from "@/services/storage/types";
import { mediaTimeFromSeconds } from "@/wasm";

/**
 * In-memory cache of resolved loop assets for saved "look" presets, keyed by
 * LookPresetMetadata.id — the exact same pattern as
 * background/custom-backgrounds-registry.ts (see that file's own doc comment for why this needs
 * to be a synchronous, session-lifetime cache rather than a zustand/React read: scene-builder.ts's
 * buildScene isn't async, and project.settings.loop only carries a stable id). Populated once
 * during EditorProvider's boot sequence (see loadPresetLoopsIntoRegistry's call site there),
 * before any project can render. Only presets that actually saved a loop file get an entry here —
 * a preset with no loop simply has nothing to look up.
 */
export interface PresetLoopAsset {
	url: string;
	file: File;
	mediaType: "image" | "video";
	/** Ticks, same unit as everywhere else in this app's timing model — used to wrap playback
	 * time for seamless repeat when the scene plays longer than one loop cycle. Undefined for an
	 * image loop (always static, no notion of a cycle) or if the source file's own duration
	 * couldn't be read at upload time. */
	durationTicks?: number;
}

const assetsById = new Map<string, PresetLoopAsset>();
let loadPromise: Promise<LookPresetMetadata[]> | null = null;

export function getPresetLoopAsset(id: string): PresetLoopAsset | undefined {
	return assetsById.get(id);
}

export function loadPresetLoopsIntoRegistry(): Promise<LookPresetMetadata[]> {
	if (loadPromise) return loadPromise;

	loadPromise = (async () => {
		const items = await storageService.loadPresets();
		for (const { metadata, loopFile } of items) {
			if (!metadata.loop || !loopFile || assetsById.has(metadata.id)) continue;
			registerPresetLoop({ presetId: metadata.id, loop: metadata.loop, file: loopFile });
		}
		return items.map((item) => item.metadata);
	})();

	return loadPromise;
}

export function registerPresetLoop({
	presetId,
	loop,
	file,
}: {
	presetId: string;
	/** `duration`, like BannerMetadata/MediaAssetData's own duration field, is in SECONDS (the raw
	 * value HTMLVideoElement.duration reports) — converted to ticks here since that's the unit
	 * every render node/resolver in this app actually works in. */
	loop: { mediaType: "image" | "video"; duration?: number };
	file: File;
}): void {
	assetsById.set(presetId, {
		url: URL.createObjectURL(file),
		file,
		mediaType: loop.mediaType,
		durationTicks:
			loop.duration !== undefined ? mediaTimeFromSeconds({ seconds: loop.duration }) : undefined,
	});
}

export function unregisterPresetLoop(presetId: string): void {
	const asset = assetsById.get(presetId);
	if (asset) {
		URL.revokeObjectURL(asset.url);
		assetsById.delete(presetId);
	}
}
